import { useState, useEffect, useRef } from 'react';
import { IconX, IconCurrencyTaka, IconCircleCheck, IconArrowDownLeft, IconArrowUpRight } from '@tabler/icons-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { notifications } from '@/lib/notifications';
import incomeExpenseService from '../../services/incomeExpenseService';
import { useOfficeSpaceStore } from '../../store/officeSpaceStore';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { toBnDigits, toEnDigits } from '../../utils/bnFormat';
import { sanitizeText } from '../../utils/sanitize';
import CustomerPicker from '../common/CustomerPicker';
import DatePickerField from '../common/DatePickerField';

// Same caps the backend enforces (name 255, note 500); names stay short on purpose.
const NAME_MAX = 80;
const NOTE_MAX = 500;
// A পরিশোধ is cash moving: পাওনা paid back = money in, দেনা paid off = money out.
const SETTLEMENT_TYPE = { pawna: 'income', dena: 'expense' };

const pad = (n) => String(n).padStart(2, '0');
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const nowHm = () => { const d = new Date(); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
// Picked day + wall-clock time → ISO instant (mirrors the income/expense modal).
function ymdTimeToIso(ymd, hm) {
    const [y, m, d] = ymd.split('-').map(Number);
    const [hh, mm] = (hm || '00:00').split(':').map(Number);
    const dt = new Date(y, m - 1, d, hh || 0, mm || 0);
    return isNaN(dt.getTime()) ? null : dt.toISOString();
}

/**
 * দেনা পাওনা "পরিশোধ" — records a পাওনা paid back to the shop, or a দেনা the shop paid off.
 * Customer first (pick / type a name / leave empty); a linked customer's outstanding
 * পাওনা / দেনা shows right under it and one tap fills that amount.
 *
 * Props: open, entry (a settlement row to edit, or null), onClose(saved: boolean).
 */
export default function SettlementModal({ open, entry = null, onClose }) {
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const activeOfficeSpaceId = useOfficeSpaceStore((s) => s.selectedOfficeSpace?.id);
    const isEditing = !!entry?.id;

    const [customerId, setCustomerId]     = useState(null);
    const [customerName, setCustomerName] = useState('');
    const [settles, setSettles]           = useState('pawna');
    const [amount, setAmount]             = useState('');
    const [date, setDate]                 = useState(() => toYmd(new Date()));
    const [time, setTime]                 = useState(nowHm);
    const [note, setNote]                 = useState('');
    const [balance, setBalance]           = useState(null); // { pawna, dena } for the linked customer
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [amountError, setAmountError]   = useState('');
    const [saving, setSaving]             = useState(false);
    const amountRef = useRef(null);
    // Picking a customer auto-selects the side they owe on — but never after the user chose one.
    const settlesTouchedRef = useRef(false);

    const defaultNames = [t('incomeExpense.settlePawna'), t('incomeExpense.settleDena')];
    const today = toYmd(new Date());

    useEffect(() => {
        if (!open) return;
        const d = entry?.createdAt ? new Date(entry.createdAt) : null;
        const linkedName = entry?.customer?.applicant_name_bn || entry?.customer?.applicant_name_en || '';
        setCustomerId(entry?.customerId ?? null);
        // An unlinked row stores the typed name; the default label means "no name was typed".
        setCustomerName(entry ? (linkedName || (defaultNames.includes(entry.name) ? '' : entry.name || '')) : '');
        setSettles(entry?.settlementOf || 'pawna');
        setAmount(entry?.amount != null ? (isBn ? toBnDigits(entry.amount) : String(entry.amount)) : '');
        setDate(d && !isNaN(d) ? toYmd(d) : toYmd(new Date()));
        setTime(d && !isNaN(d) ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : nowHm());
        setNote(entry?.note || '');
        setAmountError('');
        settlesTouchedRef.current = isEditing;
        setTimeout(() => amountRef.current?.focus(), 50);
    }, [open, entry]); // eslint-disable-line react-hooks/exhaustive-deps

    // Outstanding for the linked customer = their baki minus what was already settled.
    // When editing, this row's own amount is added back so it doesn't count against itself.
    useEffect(() => {
        if (!open || !customerId) { setBalance(null); return; }
        let cancelled = false;
        setBalanceLoading(true);
        incomeExpenseService.summary({ customerId })
            .then((res) => {
                if (cancelled) return;
                const s = res?.data || {};
                const own = isEditing && entry.customerId === customerId ? entry : null;
                const pawna = (s.totalDueIncome || 0) - (s.totalSettledPawna || 0) + (own?.settlementOf === 'pawna' ? own.amount : 0);
                const dena  = (s.totalDueExpense || 0) - (s.totalSettledDena || 0) + (own?.settlementOf === 'dena' ? own.amount : 0);
                const next = { pawna: Math.max(0, pawna), dena: Math.max(0, dena) };
                setBalance(next);
                if (!settlesTouchedRef.current) {
                    if (next.pawna > 0 && next.dena <= 0) setSettles('pawna');
                    else if (next.dena > 0 && next.pawna <= 0) setSettles('dena');
                }
            })
            .catch(() => { if (!cancelled) setBalance(null); })
            .finally(() => { if (!cancelled) setBalanceLoading(false); });
        return () => { cancelled = true; };
    }, [open, customerId]); // eslint-disable-line react-hooks/exhaustive-deps

    const money = (n) => {
        const s = Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
        return `৳${isBn ? toBnDigits(s) : s}`;
    };
    const numAmount = parseFloat(toEnDigits(amount));
    const outstanding = balance ? balance[settles] : null;
    const overpaid = outstanding != null && !isNaN(numAmount) && numAmount > outstanding + 0.005;

    const chooseSide = (side) => { settlesTouchedRef.current = true; setSettles(side); };
    const fillFrom = (side) => {
        chooseSide(side);
        const v = balance?.[side] || 0;
        if (v > 0) { setAmount(isBn ? toBnDigits(v) : String(v)); setAmountError(''); }
        amountRef.current?.focus();
    };

    const handleSave = async () => {
        if (saving) return;
        if (!amount || isNaN(numAmount) || numAmount <= 0) {
            setAmountError(t('incomeExpense.amountRequired'));
            amountRef.current?.focus();
            return;
        }
        const cleanName = sanitizeText(customerName, { maxLen: NAME_MAX });
        const cleanNote = sanitizeText(note, { maxLen: NOTE_MAX });
        const name = cleanName || t(settles === 'pawna' ? 'incomeExpense.settlePawna' : 'incomeExpense.settleDena');
        setSaving(true);
        try {
            if (isEditing) {
                await incomeExpenseService.update(entry.id, {
                    name, amount: numAmount, settlementOf: settles, type: SETTLEMENT_TYPE[settles], isDue: false,
                    note: cleanNote || null, customerId: customerId || null,
                    ...(date ? { date: ymdTimeToIso(date, time) } : {}),
                });
            } else {
                await incomeExpenseService.create({
                    name, type: SETTLEMENT_TYPE[settles], amount: numAmount, settlementOf: settles,
                    isDue: false, category: 'manual', note: cleanNote || null,
                    ...(customerId ? { customerId } : {}),
                    ...(activeOfficeSpaceId ? { officeSpaceId: activeOfficeSpaceId } : {}),
                    // Today keeps the real server timestamp; a back-dated day gets now's clock time.
                    ...(date && date !== today ? { date: ymdTimeToIso(date, time) } : {}),
                });
            }
            notifications.show({ message: t(isEditing ? 'incomeExpense.updated' : 'incomeExpense.saved'), color: 'green' });
            onClose?.(true);
        } catch (err) {
            notifications.show({ message: err.message || t('common.error'), color: 'red' });
        } finally {
            setSaving(false);
        }
    };

    const inputCls = 'w-full px-3.5 py-2.5 text-[13.5px] border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/40';
    const sides = [
        { key: 'pawna', label: t('incomeExpense.settlePawna'), hint: t('incomeExpense.settlePawnaHint'), Icon: IconArrowDownLeft },
        { key: 'dena',  label: t('incomeExpense.settleDena'),  hint: t('incomeExpense.settleDenaHint'),  Icon: IconArrowUpRight },
    ];

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(false); }}>
            <DialogContent
                showClose={false}
                aria-describedby={undefined}
                // Outside clicks don't close (half-filled entries got lost that way) — × or Esc only.
                onInteractOutside={(e) => e.preventDefault()}
                className="w-[400px] max-w-[calc(100vw-20px)] p-0 rounded-2xl"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-[18px] py-[14px] text-white bg-indigo-600 rounded-t-2xl">
                    <div className="flex items-center gap-2.5">
                        <div className="w-[30px] h-[30px] rounded-[9px] bg-white/20 flex items-center justify-center">
                            <IconCircleCheck size={17} />
                        </div>
                        <DialogTitle className="text-[15px] font-bold text-white">
                            {t(isEditing ? 'incomeExpense.settlementEditTitle' : 'incomeExpense.settlementAddTitle')}
                        </DialogTitle>
                    </div>
                    <button
                        type="button"
                        onClick={() => onClose?.(false)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                    >
                        <IconX size={15} />
                    </button>
                </div>

                <div className="px-[18px] pt-4 pb-[18px] space-y-3">
                    {/* 1 — Customer (optional): pick a saved one, type a name, or leave empty */}
                    <CustomerPicker
                        value={customerName}
                        linkedId={customerId}
                        maxLength={NAME_MAX}
                        onChange={(v) => { setCustomerName(v); setCustomerId(null); }}
                        onSelect={(m) => { setCustomerId(m.id); setCustomerName(m.name); }}
                        onClear={() => { setCustomerName(''); setCustomerId(null); }}
                        placeholder={t('incomeExpense.settlementCustomer')}
                        inputClassName={`${inputCls} pr-14`}
                    />

                    {/* 2 — The linked customer's outstanding পাওনা / দেনা; tap one to fill it */}
                    {customerId && (
                        balanceLoading ? (
                            <p className="text-xs text-zinc-400">{t('common.loading')}</p>
                        ) : balance && (balance.pawna > 0 || balance.dena > 0) ? (
                            <div className="grid grid-cols-2 gap-2">
                                {balance.pawna > 0 && (
                                    <button type="button" onClick={() => fillFrom('pawna')}
                                        className="text-left rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 hover:border-amber-400 transition-colors">
                                        <span className="block text-[11px] font-medium text-amber-600 dark:text-amber-400">{t('incomeExpense.outstandingPawna')}</span>
                                        <span className="block text-base font-bold tabular-nums text-amber-700 dark:text-amber-300">{money(balance.pawna)}</span>
                                    </button>
                                )}
                                {balance.dena > 0 && (
                                    <button type="button" onClick={() => fillFrom('dena')}
                                        className="text-left rounded-lg border border-orange-200 dark:border-orange-800/50 bg-orange-50 dark:bg-orange-900/20 px-3 py-2 hover:border-orange-400 transition-colors">
                                        <span className="block text-[11px] font-medium text-orange-600 dark:text-orange-400">{t('incomeExpense.outstandingDena')}</span>
                                        <span className="block text-base font-bold tabular-nums text-orange-700 dark:text-orange-300">{money(balance.dena)}</span>
                                    </button>
                                )}
                            </div>
                        ) : balance ? (
                            <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('incomeExpense.noOutstanding')}</p>
                        ) : null
                    )}

                    {/* 3 — What is being paid off */}
                    <div className="flex gap-2 rounded-[11px] p-1 bg-zinc-100 dark:bg-zinc-800">
                        {sides.map(({ key, label, hint, Icon }) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => chooseSide(key)}
                                className={`flex-1 flex flex-col items-center gap-0.5 py-2 rounded-[9px] transition-all ${
                                    settles === key
                                        ? 'bg-indigo-600 text-white shadow-[0_2px_8px_rgba(79,70,229,0.3)]'
                                        : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'
                                }`}
                            >
                                <span className="flex items-center gap-1 text-[13.5px] font-semibold"><Icon size={15} />{label}</span>
                                <span className={`text-[11px] ${settles === key ? 'text-white/80' : 'text-zinc-400'}`}>{hint}</span>
                            </button>
                        ))}
                    </div>

                    {/* 4 — Amount */}
                    <div>
                        <div className={`flex items-center gap-1 border rounded-[11px] px-3 h-[52px] bg-white dark:bg-zinc-800 focus-within:ring-2 ${amountError ? 'border-red-400 focus-within:ring-red-400/40' : 'border-zinc-200 dark:border-zinc-700 focus-within:ring-indigo-400/40'}`}>
                            <IconCurrencyTaka size={20} className="text-zinc-400 shrink-0" />
                            <input
                                ref={amountRef}
                                type="text"
                                inputMode="decimal"
                                maxLength={12}
                                value={amount}
                                onChange={(e) => { setAmount(e.target.value.replace(/[^0-9০-৯.]/g, '')); setAmountError(''); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.isComposing) handleSave(); }}
                                placeholder={isBn ? '০' : '0'}
                                className="flex-1 min-w-0 bg-transparent border-none outline-none text-2xl font-bold text-center text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-300"
                            />
                        </div>
                        {amountError && <p className="text-[11px] font-medium text-red-500 mt-1">{amountError}</p>}
                        {!amountError && overpaid && (
                            <p className="text-[11px] font-medium text-red-500 mt-1">{t('incomeExpense.overpayWarning', { amount: money(outstanding) })}</p>
                        )}
                    </div>

                    {/* 5 — Date (back-datable) + note */}
                    <DatePickerField
                        value={new Date(`${date}T00:00:00`)}
                        onChange={(d) => setDate(d ? toYmd(d) : today)}
                        disabled={{ after: new Date() }}
                        triggerClassName={inputCls}
                    />
                    <input
                        type="text"
                        value={note}
                        maxLength={NOTE_MAX}
                        onChange={(e) => setNote(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.isComposing) handleSave(); }}
                        placeholder={t('incomeExpense.notePlaceholder')}
                        className={inputCls}
                    />

                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="w-full py-3 rounded-[11px] text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                        {saving ? t('incomeExpense.saving') : t('incomeExpense.save')}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
